import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Brain, TrendingUp, Target, Clock, Users, AlertTriangle,
  CheckCircle, ChevronRight, Loader2, BarChart3, Zap,
  Calendar, Sun, Moon, Sunrise, Heart, Flame, Award,
  ArrowUp, ArrowDown, Minus, RefreshCw, Lightbulb
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('session_token')}`
});

const MLAnalyticsDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('summary');
  const [summary, setSummary] = useState(null);
  const [errorPatterns, setErrorPatterns] = useState(null);
  const [burnout, setBurnout] = useState(null);
  const [cohort, setCohort] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [learningPath, setLearningPath] = useState(null);

  useEffect(() => {
    loadSummary();
  }, []);

  const loadSummary = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/ml/summary`, { headers: getAuthHeaders() });
      setSummary(res.data);
    } catch (error) {
      console.error('Error loading ML summary:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadErrorPatterns = async () => {
    try {
      const res = await axios.get(`${API}/ml/error-patterns`, { headers: getAuthHeaders() });
      setErrorPatterns(res.data);
    } catch (error) {
      toast.error('Error al cargar patrones de error');
    }
  };

  const loadBurnout = async () => {
    try {
      const res = await axios.get(`${API}/ml/burnout-prediction`, { headers: getAuthHeaders() });
      setBurnout(res.data);
    } catch (error) {
      toast.error('Error al cargar predicción de burnout');
    }
  };

  const loadCohort = async () => {
    try {
      const res = await axios.get(`${API}/ml/cohort-analysis`, { headers: getAuthHeaders() });
      setCohort(res.data);
    } catch (error) {
      toast.error('Error al cargar análisis de cohorte');
    }
  };

  const loadSchedule = async () => {
    try {
      const res = await axios.get(`${API}/ml/optimal-schedule`, { headers: getAuthHeaders() });
      setSchedule(res.data);
    } catch (error) {
      toast.error('Error al cargar horarios óptimos');
    }
  };

  const loadLearningPath = async () => {
    try {
      const res = await axios.get(`${API}/ml/learning-path?days_ahead=7`, { headers: getAuthHeaders() });
      setLearningPath(res.data);
    } catch (error) {
      toast.error('Error al cargar ruta de aprendizaje');
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'patterns' && !errorPatterns) loadErrorPatterns();
    if (tab === 'burnout' && !burnout) loadBurnout();
    if (tab === 'cohort' && !cohort) loadCohort();
    if (tab === 'schedule' && !schedule) loadSchedule();
    if (tab === 'path' && !learningPath) loadLearningPath();
  };

  const getHealthColor = (score) => {
    if (score >= 80) return 'text-emerald-600';
    if (score >= 60) return 'text-amber-600';
    return 'text-red-600';
  };

  const getHealthBg = (score) => {
    if (score >= 80) return 'from-emerald-500 to-teal-500';
    if (score >= 60) return 'from-amber-500 to-orange-500';
    return 'from-red-500 to-rose-500';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Brain className="w-6 h-6 text-indigo-600" />
            Análisis Inteligente
          </h2>
          <p className="text-slate-600">Sistema ML que te ayuda a estudiar mejor</p>
        </div>
        <Button variant="outline" onClick={loadSummary}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Health Score Card */}
      {summary && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`bg-gradient-to-r ${getHealthBg(summary.health_score)} rounded-2xl p-6 text-white`}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/80 text-sm mb-1">Tu Puntuación de Salud de Estudio</p>
              <p className="text-5xl font-bold">{summary.health_score}/100</p>
              <p className="text-white/80 text-sm mt-2">
                {summary.health_score >= 80 ? '¡Excelente ritmo de estudio!' :
                 summary.health_score >= 60 ? 'Buen progreso, pero hay margen de mejora' :
                 'Necesitas ajustar tu estrategia de estudio'}
              </p>
            </div>
            <div className="text-right">
              <Heart className="w-16 h-16 text-white/30" />
            </div>
          </div>

          {/* Priority Actions */}
          {summary.priorities?.length > 0 && (
            <div className="mt-4 pt-4 border-t border-white/20">
              <p className="text-sm font-medium mb-2">Acciones prioritarias:</p>
              <div className="flex flex-wrap gap-2">
                {summary.priorities.map((p, i) => (
                  <span key={i} className="px-3 py-1 bg-white/20 rounded-full text-sm">
                    {p.action}
                  </span>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {[
          { id: 'summary', label: 'Resumen', icon: BarChart3 },
          { id: 'patterns', label: 'Patrones de Error', icon: Target },
          { id: 'burnout', label: 'Bienestar', icon: Heart },
          { id: 'cohort', label: 'Tu Posición', icon: Users },
          { id: 'schedule', label: 'Horarios', icon: Clock },
          { id: 'path', label: 'Ruta de Estudio', icon: TrendingUp }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => handleTabChange(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
              activeTab === tab.id
                ? 'bg-indigo-600 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-50 border'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-xl border p-6">
        {/* Summary Tab */}
        {activeTab === 'summary' && summary && (
          <div className="space-y-6">
            <h3 className="font-semibold text-lg">Resumen de tu análisis</h3>
            
            <div className="grid md:grid-cols-4 gap-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-5 h-5 text-rose-500" />
                  <span className="text-sm text-slate-600">Patrones detectados</span>
                </div>
                <p className="text-2xl font-bold">{summary.summary?.error_patterns?.total || 0}</p>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Heart className="w-5 h-5 text-pink-500" />
                  <span className="text-sm text-slate-600">Riesgo Burnout</span>
                </div>
                <p className={`text-2xl font-bold capitalize ${
                  summary.summary?.burnout?.risk_level === 'critical' ? 'text-red-600' :
                  summary.summary?.burnout?.risk_level === 'warning' ? 'text-amber-600' : 'text-emerald-600'
                }`}>
                  {summary.summary?.burnout?.risk_level || 'N/A'}
                </p>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5 text-indigo-500" />
                  <span className="text-sm text-slate-600">Posición</span>
                </div>
                <p className="text-2xl font-bold">
                  {summary.summary?.cohort?.position ? `#${summary.summary.cohort.position}` : 'N/A'}
                  <span className="text-sm text-slate-500 font-normal">
                    /{summary.summary?.cohort?.total || 0}
                  </span>
                </p>
              </div>
              
              <div className="p-4 bg-slate-50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-5 h-5 text-amber-500" />
                  <span className="text-sm text-slate-600">Mejor horario</span>
                </div>
                <p className="text-2xl font-bold">{summary.summary?.schedule?.best_time || 'N/A'}</p>
              </div>
            </div>

            <div className="bg-indigo-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Lightbulb className="w-5 h-5 text-indigo-600" />
                <span className="font-medium text-indigo-900">Explora cada sección</span>
              </div>
              <p className="text-sm text-indigo-700">
                Haz clic en las pestañas de arriba para ver el análisis detallado de cada área.
                El sistema aprende de tus patrones para darte recomendaciones personalizadas.
              </p>
            </div>
          </div>
        )}

        {/* Error Patterns Tab */}
        {activeTab === 'patterns' && (
          <div className="space-y-6">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <Target className="w-5 h-5 text-rose-500" />
              Patrones de Error Detectados
            </h3>
            
            {!errorPatterns ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-indigo-600 mb-2" />
                <p className="text-slate-500">Analizando tus respuestas...</p>
              </div>
            ) : !errorPatterns.sufficient_data ? (
              <div className="text-center py-8 bg-slate-50 rounded-lg">
                <Target className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-600">{errorPatterns.message}</p>
              </div>
            ) : (
              <div className="space-y-4">
                {errorPatterns.patterns?.map((pattern, idx) => (
                  <div key={idx} className={`p-4 rounded-lg border-l-4 ${
                    pattern.severity === 'high' ? 'bg-red-50 border-red-500' :
                    pattern.severity === 'medium' ? 'bg-amber-50 border-amber-500' :
                    'bg-blue-50 border-blue-500'
                  }`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-medium">{pattern.description}</h4>
                        <p className="text-sm text-slate-600 mt-1">
                          Tipo: {pattern.pattern_type}
                        </p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        pattern.severity === 'high' ? 'bg-red-200 text-red-800' :
                        pattern.severity === 'medium' ? 'bg-amber-200 text-amber-800' :
                        'bg-blue-200 text-blue-800'
                      }`}>
                        {pattern.severity}
                      </span>
                    </div>
                    
                    {pattern.data && Array.isArray(pattern.data) && (
                      <div className="mt-3 space-y-1">
                        {pattern.data.slice(0, 5).map((item, i) => (
                          <div key={i} className="flex items-center justify-between text-sm">
                            <span>{item.name}</span>
                            <span className="font-medium text-red-600">{item.error_rate}% errores</span>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {pattern.recommendations && (
                      <div className="mt-3 pt-3 border-t border-slate-200">
                        <p className="text-sm font-medium mb-1">Recomendaciones:</p>
                        <ul className="text-sm text-slate-600 space-y-1">
                          {pattern.recommendations.filter(r => r).map((rec, i) => (
                            <li key={i}>• {rec}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
                
                {errorPatterns.patterns?.length === 0 && (
                  <div className="text-center py-8 bg-emerald-50 rounded-lg">
                    <CheckCircle className="w-12 h-12 mx-auto text-emerald-500 mb-3" />
                    <p className="text-emerald-700 font-medium">¡No se detectaron patrones problemáticos!</p>
                    <p className="text-sm text-emerald-600">Tu rendimiento es consistente.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Burnout Tab */}
        {activeTab === 'burnout' && (
          <div className="space-y-6">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <Heart className="w-5 h-5 text-pink-500" />
              Predicción de Bienestar
            </h3>
            
            {!burnout ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-indigo-600" />
              </div>
            ) : (
              <>
                <div className={`p-6 rounded-xl ${
                  burnout.risk_level === 'critical' ? 'bg-red-50 border-2 border-red-300' :
                  burnout.risk_level === 'warning' ? 'bg-amber-50 border-2 border-amber-300' :
                  'bg-emerald-50 border-2 border-emerald-300'
                }`}>
                  <div className="flex items-center gap-4">
                    <div className={`w-16 h-16 rounded-full flex items-center justify-center ${
                      burnout.risk_level === 'critical' ? 'bg-red-200' :
                      burnout.risk_level === 'warning' ? 'bg-amber-200' :
                      'bg-emerald-200'
                    }`}>
                      <span className="text-3xl font-bold">{burnout.burnout_score}</span>
                    </div>
                    <div>
                      <h4 className="text-xl font-bold">{burnout.message}</h4>
                      <p className="text-slate-600">Próximo análisis recomendado: {burnout.next_check_recommended}</p>
                    </div>
                  </div>
                </div>
                
                {burnout.indicators?.length > 0 && (
                  <div>
                    <h4 className="font-medium mb-3">Indicadores detectados:</h4>
                    <div className="space-y-2">
                      {burnout.indicators.map((ind, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                          <span>{ind.description}</span>
                          <span className="text-sm text-slate-500">+{ind.weight} puntos</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div>
                  <h4 className="font-medium mb-3">Recomendaciones:</h4>
                  <div className="grid md:grid-cols-2 gap-2">
                    {burnout.recommendations?.map((rec, i) => (
                      <div key={i} className="p-3 bg-indigo-50 rounded-lg text-sm text-indigo-800">
                        {rec}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* Cohort Tab */}
        {activeTab === 'cohort' && (
          <div className="space-y-6">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-500" />
              Análisis de Cohorte
            </h3>
            
            {!cohort ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-indigo-600" />
              </div>
            ) : !cohort.sufficient_data ? (
              <div className="text-center py-8 bg-slate-50 rounded-lg">
                <Users className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-600">{cohort.message}</p>
              </div>
            ) : (
              <>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl p-4 text-white">
                    <p className="text-white/80 text-sm">Tu posición</p>
                    <p className="text-4xl font-bold">#{cohort.your_position?.rank}</p>
                    <p className="text-white/80 text-sm">de {cohort.your_position?.total} estudiantes</p>
                  </div>
                  
                  <div className="bg-white rounded-xl p-4 border">
                    <p className="text-slate-600 text-sm">Tu media</p>
                    <p className="text-3xl font-bold text-slate-900">{cohort.your_stats?.avg_score}%</p>
                    <p className="text-sm text-slate-500">{cohort.your_stats?.total_exams} exámenes</p>
                  </div>
                  
                  <div className="bg-white rounded-xl p-4 border">
                    <p className="text-slate-600 text-sm">Media del grupo</p>
                    <p className="text-3xl font-bold text-slate-900">{cohort.cohort_stats?.avg_score}%</p>
                    <p className="text-sm text-slate-500">Top 10%: {cohort.cohort_stats?.top_10_avg}%</p>
                  </div>
                </div>
                
                {cohort.insights?.map((insight, i) => (
                  <div key={i} className={`p-4 rounded-lg ${
                    insight.type === 'top_performer' ? 'bg-emerald-50 border border-emerald-200' :
                    insight.type === 'needs_improvement' ? 'bg-amber-50 border border-amber-200' :
                    'bg-blue-50 border border-blue-200'
                  }`}>
                    <h4 className="font-medium">{insight.title}</h4>
                    <p className="text-sm text-slate-600 mt-1">{insight.description}</p>
                    <p className="text-sm font-medium mt-2">→ {insight.action}</p>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* Schedule Tab */}
        {activeTab === 'schedule' && (
          <div className="space-y-6">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-amber-500" />
              Horarios Óptimos
            </h3>
            
            {!schedule ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-indigo-600" />
              </div>
            ) : !schedule.sufficient_data ? (
              <div className="text-center py-8 bg-slate-50 rounded-lg">
                <Clock className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-slate-600">{schedule.message}</p>
                <p className="text-sm text-slate-500 mt-1">
                  Exámenes completados: {schedule.exams_completed}/{schedule.exams_needed}
                </p>
              </div>
            ) : (
              <>
                <div className="bg-gradient-to-r from-amber-100 to-orange-100 rounded-xl p-6">
                  <div className="flex items-center gap-3 mb-2">
                    {schedule.chronotype === 'mañanas' ? <Sunrise className="w-8 h-8 text-amber-600" /> :
                     schedule.chronotype === 'noches' ? <Moon className="w-8 h-8 text-indigo-600" /> :
                     <Sun className="w-8 h-8 text-orange-600" />}
                    <div>
                      <p className="text-lg font-bold text-slate-900">Eres de {schedule.chronotype}</p>
                      <p className="text-sm text-slate-600">Tu cuerpo rinde mejor en este horario</p>
                    </div>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h4 className="font-medium mb-3 text-emerald-700">✅ Mejores horas</h4>
                    <div className="space-y-2">
                      {schedule.best_hours?.map((h, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
                          <span className="font-medium">{h.hour_label}</span>
                          <span className="text-emerald-600 font-bold">{h.avg_score}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <h4 className="font-medium mb-3 text-red-700">⚠️ Horas a evitar</h4>
                    <div className="space-y-2">
                      {schedule.worst_hours?.slice().reverse().map((h, i) => (
                        <div key={i} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                          <span className="font-medium">{h.hour_label}</span>
                          <span className="text-red-600 font-bold">{h.avg_score}%</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                
                {schedule.insights?.map((insight, i) => (
                  <div key={i} className="p-4 bg-indigo-50 rounded-lg">
                    <h4 className="font-medium text-indigo-900">{insight.title}</h4>
                    <p className="text-sm text-indigo-700 mt-1">{insight.description}</p>
                    <p className="text-sm font-medium text-indigo-800 mt-2">💡 {insight.recommendation}</p>
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* Learning Path Tab */}
        {activeTab === 'path' && (
          <div className="space-y-6">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-500" />
              Tu Ruta de Aprendizaje (7 días)
            </h3>
            
            {!learningPath ? (
              <div className="text-center py-8">
                <Loader2 className="w-8 h-8 animate-spin mx-auto text-indigo-600" />
              </div>
            ) : (
              <>
                <div className="grid md:grid-cols-4 gap-4 mb-4">
                  <div className="p-3 bg-indigo-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-indigo-600">{learningPath.total_tasks}</p>
                    <p className="text-xs text-slate-600">Tareas totales</p>
                  </div>
                  <div className="p-3 bg-emerald-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-emerald-600">{learningPath.total_estimated_hours}h</p>
                    <p className="text-xs text-slate-600">Tiempo estimado</p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-amber-600">{learningPath.task_breakdown?.flashcards || 0}</p>
                    <p className="text-xs text-slate-600">Repasos flashcards</p>
                  </div>
                  <div className="p-3 bg-rose-50 rounded-lg text-center">
                    <p className="text-2xl font-bold text-rose-600">{learningPath.focus_areas?.length || 0}</p>
                    <p className="text-xs text-slate-600">Áreas de foco</p>
                  </div>
                </div>
                
                {learningPath.focus_areas?.length > 0 && (
                  <div className="p-4 bg-rose-50 rounded-lg">
                    <p className="font-medium text-rose-900 mb-2">🎯 Temas prioritarios:</p>
                    <div className="flex flex-wrap gap-2">
                      {learningPath.focus_areas.map((area, i) => (
                        <span key={i} className="px-3 py-1 bg-rose-200 text-rose-800 rounded-full text-sm">
                          {area}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="space-y-4">
                  {[1, 2, 3, 4, 5, 6, 7].map(day => {
                    const dayTasks = learningPath.tasks?.filter(t => t.day === day) || [];
                    if (dayTasks.length === 0) return null;
                    
                    return (
                      <div key={day} className="border rounded-lg overflow-hidden">
                        <div className="bg-slate-100 px-4 py-2 font-medium">
                          Día {day}
                        </div>
                        <div className="divide-y">
                          {dayTasks.map((task, i) => (
                            <div key={i} className="p-3 flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <span className={`w-2 h-2 rounded-full ${
                                  task.priority === 'high' ? 'bg-red-500' :
                                  task.priority === 'medium' ? 'bg-amber-500' : 'bg-green-500'
                                }`} />
                                <div>
                                  <p className="font-medium text-sm">{task.topic_name}</p>
                                  <p className="text-xs text-slate-500">{task.reason}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <span className={`px-2 py-1 rounded text-xs ${
                                  task.task_type === 'flashcards' ? 'bg-purple-100 text-purple-700' :
                                  task.task_type === 'practice' ? 'bg-red-100 text-red-700' :
                                  task.task_type === 'quick_test' ? 'bg-blue-100 text-blue-700' :
                                  'bg-slate-100 text-slate-700'
                                }`}>
                                  {task.task_type}
                                </span>
                                <p className="text-xs text-slate-500 mt-1">{task.estimated_minutes} min</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MLAnalyticsDashboard;
